import { useEffect, useState } from "react";
import axiosInstant from "../../../api";
import { editProject } from "../../../api/endpoints";
import { apiErrorCode } from "../../../utils/apiError";
import { resolveProjectBrandId } from "../../../utils";
import { BRAND_PROJECT_STATUSES } from "../helper";
import { BrandProject } from "./constants";

/**
 * Soft delete + restore for projects (admin / AM admin only — callers gate
 * with isAM(role)). DELETE /brand-projects/:id hides the project and its
 * videos; PATCH { deactivated: false } on the edit route brings both back.
 * Where a restored project LANDS is the server's call; the copy below only
 * explains the rules and reports what the response says happened.
 */

/** The body of a successful restore. */
export interface RestoredProject {
    id?: number;
    deactivated?: boolean;
    order_id?: number | null;
    previous_status?: string | null;
    status?: string | null;
    rejoined?: boolean;
    reverted_to_scheduling?: boolean;
    /** It couldn't rejoin its old expert selection and is on one of its own. */
    own_selection?: boolean;
}

/**
 * The toast text for a failed delete or restore: the server's message, then
 * its error, then the transport error. control()-wrapped routes send a bare
 * code string ("BrandProjectNotFound") when there are no details; a string
 * body that isn't a code (an HTML error page) is ignored. `codeCopy` swaps a
 * code for plain copy; it is per action, because one code can mean different
 * things (a failed DELETE also answers BrandProjectNotDeleted, with a 500).
 */
export const softDeleteErrorMessage = (
    err: unknown,
    fallback: string,
    codeCopy: Record<string, string> = {}
): string => {
    const e = err as { message?: string; response?: { data?: unknown } } | null | undefined;
    const data = e && e.response ? e.response.data : undefined;
    let code: string | undefined;
    if (data && typeof data === "object") {
        const { message, error } = data as { message?: unknown; error?: unknown };
        if (typeof message === "string" && message) return message;
        if (typeof error === "string" && error) code = error;
    } else if (typeof data === "string" && /^[A-Za-z_]+$/.test(data)) {
        code = data;
    }
    if (code) return codeCopy[code] || code;
    return (e && e.message) || fallback;
};

// Restore (409) codes whose body carries no message of its own.
const RESTORE_ERROR_COPY: Record<string, string> = {
    OrderDeactivated: "This project belongs to a deleted order. Restore the order first, then the project if it is still deleted.",
    BrandProjectNotDeleted: "This project has already been restored."
};

export const restoreErrorMessage = (err: unknown): string =>
    softDeleteErrorMessage(err, "Couldn't restore the project. Please try again.", RESTORE_ERROR_COPY);

export const isAlreadyRestoredError = (err: unknown): boolean =>
    apiErrorCode(err) === "BrandProjectNotDeleted";

/**
 * Whether a list row is a deleted project. The row's own flag wins over the
 * tab it is shown in: usePagination has no request keying, so a slow
 * response from the other tab can land after a switch, and its rows must
 * keep the actions that fit them.
 */
export const isDeletedProjectRow = (project: BrandProject, inDeletedView: boolean): boolean => {
    const { deactivated } = project as BrandProject & { deactivated?: unknown };
    return typeof deactivated === "boolean" ? deactivated : inDeletedView;
};

// The API ignores the brandId segment of the edit route, so any brand on the
// row will do. List rows come without brand_id (the list excludes it).
const restoreBrandId = (project: BrandProject): number | undefined => {
    const id =
        resolveProjectBrandId(project) ||
        (project.brand && project.brand.id) ||
        (project.brands || []).find((b) => b && b.id)?.id;
    return id ? Number(id) : undefined;
};

export const restoreDeletedProject = async (project: BrandProject): Promise<RestoredProject> => {
    const brandId = restoreBrandId(project);
    if (!brandId || !project?.id) {
        throw new Error("This project has no brand on it, so it can't be restored from here.");
    }
    // ONLY the flag: the API refuses a restore that rides along with an edit.
    const res = await axiosInstant.patch(editProject(brandId, Number(project.id)), {
        deactivated: false
    });
    return (res && res.data) || {};
};

// Mirrors RESTORE_TO_SCHEDULING_STATUSES in the API (brandProjects.js): the
// statuses whose SOWs the delete voided.
const RESTORE_TO_SCHEDULING_STATUSES: string[] = [
    BRAND_PROJECT_STATUSES.PRE_PRODUCTION,
    BRAND_PROJECT_STATUSES.FILMING,
    BRAND_PROJECT_STATUSES.EDITING,
    BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED
];

/** The restore confirmation's copy, one paragraph per entry. */
export const restoreConfirmationCopy = (project: BrandProject): string[] => {
    const title = project.title || "This project";
    const status = project.status;
    const intro = `"${title}" will be restored with the videos deleted with it. Videos deleted on their own before it stay deleted.`;

    if (status === BRAND_PROJECT_STATUSES.CANCELLED) {
        return [intro, "It was Cancelled when it was deleted, so it comes back Cancelled."];
    }
    const rejoin =
        "If live projects share its expert selection, it rejoins them (at their status, or at its own once filming is committed). If the selection changed while it was deleted, it comes back on a selection of its own.";
    if (status && RESTORE_TO_SCHEDULING_STATUSES.includes(status)) {
        return [
            intro,
            `${rejoin} If none do, it goes back from ${status} to Scheduling, and the expert SOWs must be sent again.`
        ];
    }
    return [intro, `${rejoin} If none do, it keeps its status${status ? ` (${status})` : ""}.`];
};

/** The success toast: where the restore actually landed. */
export const restoredProjectMessage = (result: RestoredProject): string => {
    const { status, previous_status: previous } = result;
    if (!status) return "Project restored.";
    if (result.own_selection) {
        return `Project restored to ${status} on an expert selection of its own (its old selection changed while it was deleted).`;
    }
    if (result.rejoined) {
        return `Project restored. It rejoined its expert selection at ${status}.`;
    }
    if (result.reverted_to_scheduling) {
        return `Project restored to Scheduling${
            previous ? ` (it was at ${previous})` : ""
        }. The expert SOWs must be sent again.`;
    }
    return `Project restored to ${status}.`;
};

/** What a delete takes off live retailer pages (GET .../delete-impact). */
export interface DeleteImpact {
    pdp_videos: number;
    pdp_pages: number;
}

/**
 * Fetches the delete impact while `url` is set (the confirmation is open).
 * Best-effort: null until it lands, and on failure the dialog just goes
 * without the warning.
 */
export const useDeleteImpact = (url: string | null): DeleteImpact | null => {
    const [impact, setImpact] = useState<DeleteImpact | null>(null);
    useEffect(() => {
        setImpact(null);
        if (!url) return undefined;
        let live = true;
        axiosInstant
            .get(url)
            .then((res) => {
                if (live) setImpact(res.data);
            })
            .catch(() => {});
        return () => {
            live = false;
        };
    }, [url]);
    return impact;
};

/** The delete confirmation's warning line, or null when nothing is live on a PDP. */
export const pdpImpactWarning = (impact: DeleteImpact | null, owner: "project" | "order" | "video"): string | null => {
    if (!impact || !(impact.pdp_videos > 0)) return null;
    const n = impact.pdp_videos;
    const noun = n === 1 ? "video" : "videos";
    const pages = `${impact.pdp_pages} retailer product ${impact.pdp_pages === 1 ? "page" : "pages"}`;
    const them = impact.pdp_pages === 1 ? "it" : "them";
    if (owner === "video") return `This video is live on ${pages} and will come off ${them}.`;
    const videos = owner === "order" ? `${n} ${noun} on its projects` : `${n} of its ${noun}`;
    return `${videos} ${n === 1 ? "is" : "are"} live on ${pages} and will come off ${them}.`;
};
